<script>
import AlertIcon from "@src/shared/icons/alert-icon.svg";

export default {
  emits: ["confirm", "hidden"],
  props: {
    id: {
      type: String,
      required: true,
    },
    action: {
      type: Object,
      required: true,
    },
    confirmationFields: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      alertIcon: AlertIcon,
      step: 1,
      confirmationText: "",
    };
  },
  computed: {
    isDisabled() {
      return this.confirmationText.trim() !== this.action.confirmationPhrase;
    },
    modalClass() {
      return this.step === 2
        ? "actions-confirmation-modal actions-confirmation-modal--confirmation"
        : "actions-confirmation-modal";
    },
  },
  methods: {
    handleSubmit() {
      if (this.loading) return;

      if (this.step === 1) {
        this.step = 2;
        return;
      }

      this.$emit("confirm");
    },
    handleHidden() {
      this.step = 1;
      this.confirmationText = "";
      this.$emit("hidden");
    },
  },
};
</script>

<template>
  <b-modal
    centered
    size="md"
    :id="id"
    :modal-class="modalClass"
    :aria-label="action.title"
    hide-header
    hide-footer
    :no-close-on-backdrop="loading"
    :no-close-on-esc="loading"
    @hidden="handleHidden"
  >
    <template v-if="step === 1">
      <div class="actions-confirmation-modal__body">
        <div class="actions-confirmation-modal__header">
          <div>
            <h2>{{ action.title }}</h2>
            <p>{{ action.initialDescription }}</p>
          </div>
          <button type="button" aria-label="Fechar" :disabled="loading" @click="$bvModal.hide(id)">
            <span>&times;</span>
          </button>
        </div>
        <div
          v-if="action.alert && action.alert.description"
          class="actions-confirmation-modal__alert"
        >
          <img
            :src="alertIcon"
            alt=""
            class="actions-confirmation-modal__alert-icon"
          />
          <div>
            <strong>{{ action.alert.title }}</strong>
            <p>{{ action.alert.description }}</p>
          </div>
        </div>
      </div>
      <div class="actions-confirmation-modal__actions">
        <b-button type="button" :disabled="loading" @click="$bvModal.hide(id)">Cancelar</b-button>
        <b-button type="button" :disabled="loading" @click="handleSubmit">
          {{ action.nextLabel }}
        </b-button>
      </div>
    </template>

    <form v-else @submit.prevent="handleSubmit">
      <div class="actions-confirmation-modal__body">
        <div class="actions-confirmation-modal__header">
          <div>
            <h2>{{ action.title }}</h2>
            <p>{{ action.confirmationDescription }}</p>
          </div>
          <button type="button" aria-label="Fechar" :disabled="loading" @click="$bvModal.hide(id)">
            <span>&times;</span>
          </button>
        </div>
        <b-alert v-if="errorMessage" show variant="danger">
          {{ errorMessage }}
        </b-alert>
        <div
          v-if="confirmationFields.length"
          class="actions-confirmation-modal__data"
        >
          <div v-for="field in confirmationFields" :key="field.label">
            <span>{{ field.label }}:</span>
            <strong>{{ field.value }}</strong>
          </div>
        </div>
        <label
          class="actions-confirmation-modal__confirmation"
          :for="`${id}-confirmation`"
        >
          <span>
            Para confirmar, digite
            <strong>{{ action.confirmationPhrase }}</strong> abaixo:
          </span>
          <b-form-input
            :id="`${id}-confirmation`"
            v-model="confirmationText"
            :placeholder="`Digite ${action.confirmationPhrase}`"
            autocomplete="off"
            :disabled="loading"
          />
        </label>
      </div>
      <div class="actions-confirmation-modal__actions">
        <b-button type="button" :disabled="loading" @click="$bvModal.hide(id)">Cancelar</b-button>
        <b-button type="submit" :disabled="isDisabled || loading">
          {{ action.confirmLabel }}
        </b-button>
      </div>
    </form>
    <div v-if="loading" class="actions-confirmation-modal__loading">
      <output class="spinner-border">
        <span class="sr-only">Loading...</span>
      </output>
    </div>
  </b-modal>
</template>

<style lang="scss">
.actions-confirmation-modal {
  .modal-dialog {
    max-width: 512px;
  }
  .modal-content {
    border: 0;
    border-radius: 8px;
    overflow: hidden;
  }
  .modal-body {
    padding: 0;
    position: static;
  }
  &--confirmation .modal-content {
    min-height: 470px;
  }
  &__body {
    padding: 24px 24px 28px;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 32px;
    h2 {
      color: #000c0b;
      font-size: 18px;
      font-weight: 600;
      line-height: 24px;
      margin: 0 0 12px;
    }
    p {
      color: #666d6d;
      font-size: 16px;
      line-height: 24px;
      margin: 0;
    }
    button {
      align-items: center;
      background: #f0f2f5;
      border: 0;
      border-radius: 50%;
      color: #999e9d;
      cursor: pointer;
      display: flex;
      flex: 0 0 30px;
      font-size: 24px;
      height: 30px;
      justify-content: center;
      line-height: 1;
      padding: 0;
      &:focus-visible {
        outline: 2px solid #000c0b;
        outline-offset: 2px;
      }
    }
  }
  &__alert {
    align-items: flex-start;
    background: #fefce8;
    border: 1px solid #9e610a;
    border-radius: 8px;
    color: #9e610a;
    display: flex;
    gap: 12px;
    padding: 16px;
    strong {
      display: block;
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      margin-bottom: 4px;
    }
    p {
      font-size: 16px;
      line-height: 24px;
      margin: 0;
    }
  }
  &__alert-icon {
    flex: 0 0 18px;
    height: 18px;
    margin-top: 2px;
    width: 18px;
  }
  &__data {
    background: #f7f7f7;
    border-radius: 8px;
    margin-bottom: 28px;
    padding: 16px 20px;
    div {
      align-items: center;
      display: flex;
      font-size: 16px;
      justify-content: space-between;
      line-height: 24px;
      &:not(:last-child) {
        margin-bottom: 12px;
      }
    }
    span {
      color: #666d6d;
    }
    strong {
      color: #000c0b;
      font-weight: 500;
    }
  }
  &__confirmation {
    color: #000c0b;
    display: block;
    font-size: 14px;
    line-height: 20px;
    > span {
      display: block;
      margin-bottom: 8px;
    }
    strong {
      background: #f0f2f5;
      border-radius: 4px;
      font-weight: 600;
      padding: 2px 4px;
    }
    input {
      border: 1px solid #d9dddd;
      border-radius: 6px;
      color: #000c0b;
      font-size: 14px;
      height: 50px;
      padding: 0 16px;
    }
  }
  &__actions {
    border-top: 1px solid #e5e7e7;
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    padding: 16px 24px 24px;
    .btn {
      align-items: center;
      border: 0;
      border-radius: 8px;
      display: flex;
      font-size: 14px;
      font-weight: 600;
      height: 48px;
      justify-content: center;
      padding: 0 16px;
    }
    .btn:first-child {
      background: #f0f2f5;
      color: #000c0b;
      min-width: 100px;
    }
    .btn:last-child {
      background: #000c0b;
      color: #fff;
      min-width: 176px;
    }
    .btn:last-child:disabled {
      background: #e1e3e4;
      color: #fff;
      opacity: 1;
    }
  }
  &__loading {
    align-items: center;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 8px;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
}
</style>
